import { Box } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { cloneDeep } from "lodash";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Flex from "../../../../../../../components/Flex";
import LoadingOverlay from "../../../../../../../components/LoadingOverlay";
import StyledFormModal from "../../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../../components/app/StyledButtonV2";
import { DisclosureHost } from "../../../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../../../hooks/useSnackbar";
import { useGetAllScreeningListPrefill } from "../../../../../../../queries/encounter/screening/use-getAllScreenerAns";
import { useScreeningList } from "../../../../../../../queries/encounter/screening/use-screening-get-list";
import { useScreeningUpdate } from "../../../../../../../queries/encounter/screening/use-screening-update";
import { useResolveRequiringAction } from "../../../../../../../queries/patient-chart/requiring-action/use-resolve-requiring-action";
import {
  DEF_PAGINATION,
  getXtenantId,
  showToastOnApiCall,
} from "../../../../../../../utils/utils";
import AllScreeningAssessment from "./AllScreeningAssessment";
import { TitleMapper } from "./assessment-constant";
import useCurrentUser from "../../../../../../../hooks/local-storage/use-current-user";
interface AssessmentDrawerProps {
  disclosureHost: DisclosureHost;
  appointmentIdParam?: string;
  requiringActionId?: string;
}

export interface DataItem {
  answerOptions: string;
  question: string;
  questionId: string;
  type: string;
  uuid?: string;
}

export interface TableData {
  answerOptions: string[];
  screeners: DataItem[];
}

const AssessmentDrawer = ({
  disclosureHost,
  appointmentIdParam,
  requiringActionId,
}: AssessmentDrawerProps) => {
  const showSnackbar = useSnackbar();
  const appointmentid = useSearchParams()[0].get("appointmentid");
  const { patientId } = useParams();
  const [me] = useCurrentUser();
  const providerName = me?.firstName + " " + me?.lastName;
  const providerRole = me?.role;
  // const { mutateAsync: saveScreeningAns } = useScreeningCreate();
  const { mutateAsync: updateScreeningAns } = useScreeningUpdate();
  const { mutateAsync: resolveRequiringAction } = useResolveRequiringAction();

  const queryClient = useQueryClient();

  const resolvedAppointmentId = appointmentIdParam ?? appointmentid;

  const { data, isLoading, isRefetching } = useScreeningList({
    pagination: DEF_PAGINATION,
    gridSorting: [],
    type: disclosureHost?.data,
    xTENANTID: getXtenantId(),
  });
  const [tableData, setTableData] = useState<TableData>(data?.data);
  const { data: prefillData, isLoading: isPrefillDataLoading } =
    useGetAllScreeningListPrefill({
      type: disclosureHost?.data,
      pagination: {
        page: 0,
        pageSize: 20,
      },
      gridSorting: [],
      appointmentUuid: resolvedAppointmentId ?? undefined,
      patientId: resolvedAppointmentId ? undefined : patientId,
      xTENANTID: getXtenantId(),
    });

  const isPrefillDataAvailable =
    prefillData?.data?.content && prefillData?.data?.content?.length > 0;
  useEffect(() => {
    const transformedPrefillTableData: TableData = {
      answerOptions: data?.data?.answerOptions,
      screeners: prefillData?.data?.content?.map((it) => ({
        ...it,
        answerOptions: it?.answer,
      })) as DataItem[],
    };

    if (isPrefillDataAvailable) {
      setTableData(transformedPrefillTableData);
    } else setTableData(data?.data);
  }, [data?.data, prefillData?.data]);

  const handleRadioChange = (ind: number, val: string) => {
    const cloneTableData = cloneDeep(tableData);

    if (cloneTableData?.screeners?.[ind])
      cloneTableData.screeners[ind].answerOptions = val;

    setTableData(cloneTableData);
  };

  const reviewHandler = () => {
    showToastOnApiCall({
      apiCall: resolveRequiringAction({
        requiringActionId: requiringActionId!,
      }),
      onSave: () => {
        queryClient.invalidateQueries({
          queryKey: ["requiring-action"],
        });
        queryClient.invalidateQueries({
          queryKey: ["patient-requiring-action"],
        });
        disclosureHost.close();
      },
      showSnackbar,
    });
  };

  const saveHandler = () => {
    if (appointmentid && patientId) {
      const patientScreener = {
        screenerQuestion: tableData?.screeners?.map((it) => ({
          question: it?.uuid,
          answer: it?.answerOptions,
        })),
        appointmentId: appointmentid,
        screenerType: disclosureHost?.data,
      };

      const payload = {
        patientUuid: patientId!,
        patientScreener,
        xTENANTID: getXtenantId(),
      };

      // if (isPrefillDataAvailable) {
      const updatedScreener = tableData?.screeners?.map((it) => ({
        uuid: it?.uuid,
        question: it?.question,
        answer: it?.answerOptions,
      }));

      showToastOnApiCall({
        apiCall: updateScreeningAns({
          ...payload,
          patientScreener: {
            screenerQuestion: updatedScreener,
            appointmentId: appointmentid,
            submittedBy: "PROVIDER",
            screenerType: disclosureHost?.data,
            providerName: providerName ?? undefined,
            providerRole: providerRole ?? undefined,
          },
        }),
        onSave: onSave,
        showSnackbar,
      });
      // } else
      // showToastOnApiCall({
      //   apiCall: saveScreeningAns(payload),
      //   onSave: onSave,
      //   showSnackbar,
      // });
    }
  };

  const onSave = () => {
    queryClient.invalidateQueries({
      queryKey: ["encounter-all-screening-list"],
    });
    queryClient.invalidateQueries({
      queryKey: ["encounter-all=screening-preview"],
    });

    disclosureHost.close();
  };

  function handleClose() {
    disclosureHost.close();
  }

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={handleClose}
      dialogTitle="Add Screening"
      dialogAction={
        <Flex gap={1}>
          {requiringActionId ? (
            <StyledButtonV2
              label="Mark As Reviewed"
              variant="contained"
              onClick={reviewHandler}
            />
          ) : (
            <StyledButtonV2
              label="Save"
              variant="contained"
              onClick={saveHandler}
            />
          )}
        </Flex>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "50vw",
          height: "80vh",
        },
      }}
    >
      <Box
        sx={{
          pt: 2,
        }}
      >
        {/* <StyledDrawer
        open={disclosureHost.opened}
        onClose={disclosureHost.close}
        title={
          (DrawerTitleMapper?.[disclosureHost?.data] || "") + " Assessment"
        }
        bottomFixedAction
        drawerAction={
          <Button variant="contained" onClick={saveHandler}>
            {isPrefillDataAvailable ? "Update" : "Save"}
          </Button>
        } */}
        {/* }> */}
        <LoadingOverlay
          isLoading={isLoading || isRefetching || isPrefillDataLoading}
        />
        <AllScreeningAssessment
          tableData={tableData}
          disabled={!!requiringActionId}
          handleRadioChange={handleRadioChange}
          titleDetail={TitleMapper[disclosureHost.data]}
        />
      </Box>
    </StyledFormModal>
  );
};

export default AssessmentDrawer;
